/**
 * Per-cycle Exchange hub: all loops with plain-language status,
 * "Your turn" banner, and links to the role-aware loop view.
 */

import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  GitBranch,
  LayoutDashboard,
} from 'lucide-react';
import { PageHeader } from '../../../../components/prioritization/PageHeader';
import { useDegapProcessStore } from '../../../../stores/prioritization/degapProcessStore';
import { useCatchballLoopStore } from '../../../../stores/prioritization/catchballLoopStore';
import { useTrackStore } from '../../../../stores/prioritization/trackStore';
import { CascadePathRow } from '../../../../components/prioritization/catchball/CascadePathRow';
import { useUserStore } from '../../../../stores/prioritization/userStore';
import { useEnterpriseIdentityStore } from '../../../../stores/prioritization/enterpriseIdentityStore';

// ─── Plain-language state helpers ─────────────────────────────────────────────



function isWaitingForReview(state: string | undefined): boolean {
  if (!state) return false;
  const s = state.toUpperCase();
  return ['PROPOSED', 'RETURNED_TO_TOP'].some((x) => s.includes(x));
}

function isNeedsRevision(state: string | undefined): boolean {
  if (!state) return false;
  const s = state.toUpperCase();
  return ['REVISION_REQUESTED', 'RETURNED'].some((x) => s.includes(x));
}


export function CatchballExchangeCyclePage() {
  const { cycleId = '' } = useParams<{ cycleId: string }>();
  const cyclesRaw = useDegapProcessStore((s) => s.cycles);
  const fetchLoops = useCatchballLoopStore((s) => s.fetchFromSupabase);
  const loopsRaw = useCatchballLoopStore((s) => s.loops);
  const fetchTracks = useTrackStore((s) => s.fetchTracksForCycle);

  const cycle = useMemo(() => (cyclesRaw ?? []).find((c) => c.id === cycleId), [cyclesRaw, cycleId]);

  useEffect(() => {
    if (cycleId) {
      void fetchLoops(cycleId);
      void fetchTracks(cycleId);
    }
  }, [cycleId, fetchLoops, fetchTracks]);

  const cycleLoops = useMemo(
    () => (loopsRaw ?? []).filter((l) => l.cycleId === cycleId).sort((a, b) => a.loopNumber - b.loopNumber),
    [loopsRaw, cycleId]
  );

  // Which stops on a path this viewer answers for — drives the "in your court"
  // highlight. Membership, not role: a person is answerable at the org units they
  // belong to, wherever those sit on the path.
  const currentUserId = useUserStore((st) => st.userId);
  const orgUnitIdsByUser = useEnterpriseIdentityStore((st) => st.userOrgUnitIdsByUser);
  const viewerOrgUnitIds = useMemo(
    () => (currentUserId ? orgUnitIdsByUser.get(currentUserId) ?? [] : []),
    [currentUserId, orgUnitIdsByUser],
  );

  const waitingForReviewCount = cycleLoops.filter((l) => isWaitingForReview(l.state)).length;
  const needsRevisionCount = cycleLoops.filter((l) => isNeedsRevision(l.state)).length;

  return (
    <div className="space-y-5">
      <div>
        <Link
          to="/prioritization/cascade"
          className="inline-flex items-center gap-1.5 text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          All cycles
        </Link>
      </div>

      <PageHeader
        icon={<GitBranch className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title={cycle?.name ?? 'Cycle'}
        subtitle={`${cycleLoops.length} alignment loop${cycleLoops.length === 1 ? '' : 's'}`}
      />

      {/* Your turn banner */}
      {(waitingForReviewCount > 0 || needsRevisionCount > 0) && (
        <div className="flex items-start gap-3 border border-[var(--mustard)] dark:border-[var(--mustard)] bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 px-4 py-3">
          <AlertCircle className="h-5 w-5 text-[var(--mustard)] dark:text-[var(--mustard)] shrink-0 mt-0.5" />
          <div className="text-sm">
            {waitingForReviewCount > 0 && (
              <p className="font-medium text-[var(--mustard)] dark:text-[var(--mustard)]">
                {waitingForReviewCount} loop{waitingForReviewCount === 1 ? '' : 's'} waiting for your review
              </p>
            )}
            {needsRevisionCount > 0 && (
              <p className={`text-[var(--mustard)] dark:text-[var(--mustard)] ${waitingForReviewCount > 0 ? 'mt-0.5' : 'font-medium'}`}>
                {needsRevisionCount} loop{needsRevisionCount === 1 ? '' : 's'} sent back for revision
              </p>
            )}
          </div>
        </div>
      )}

      {!cycle && cycleId ? (
        <p className="text-sm text-[var(--mustard)] dark:text-[var(--mustard)] py-4">
          Cycle not found.{' '}
          <Link to="/prioritization/cycles" className="underline">
            Browse cycles
          </Link>
        </p>
      ) : null}

      <div className="flex gap-2">
        <Link
          to={`/prioritization/cascade/dashboard/${cycleId}`}
          className="inline-flex items-center gap-2 border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
        >
          <LayoutDashboard className="h-4 w-4" />
          Classic loops
        </Link>
        <Link
          to="/prioritization/catchball"
          className="inline-flex items-center gap-2 border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
        >
          Catchball home
        </Link>
      </div>

      {cycleLoops.length === 0 && cycleId ? (
        <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] p-8 text-center text-sm text-[var(--text-ter)]">
          No alignment loops for this cycle yet.{' '}
          <Link to={`/prioritization/cascade/compose/${cycleId}`} className="text-[var(--text-ter)] hover:underline">
            Start one with the request composer
          </Link>
        </div>
      ) : null}

      {/* The cascade, drawn as its paths.
          Replaces a status list organised by audience with the route each Loop is
          running over and where its Ball currently sits. "In your court" is a
          highlight on this view rather than a separate screen — audience was only
          ever the fallback axis once the route was out of sight. */}
      <div className="space-y-3">
        {cycleLoops.map((loop) => (
          <CascadePathRow
            key={loop.id}
            loop={loop}
            cycleId={cycleId ?? ''}
            viewerOrgUnitIds={viewerOrgUnitIds}
          />
        ))}
      </div>
    </div>
  );
}

export default CatchballExchangeCyclePage;
